import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { astrologyEndPoint } from '../../../Services/AllApi';
import { apiConnector } from '../../../Services/ApiConnector';
import { FaSpinner, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaUser, FaPhone, FaHeart, FaVenus, FaMars } from 'react-icons/fa';
import toast from 'react-hot-toast';
import HoroscopeMatchingReport from './HoroscopeMatchingReport';
import HardcodedLocationSelector from '../../common/HardcodedLocationSelector';
import { useLanguage } from '../../../i18n/LanguageContext';

const MatchMakingFeature = () => {
  const { t } = useLanguage();
  // Female details
  const [girlName, setGirlName] = useState('');
  //const [girlPhone, setGirlPhone] = useState('');
  //const [girlPhoneError, setGirlPhoneError] = useState('');
  const [girlSelectedDate, setGirlSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [girlSelectedTime, setGirlSelectedTime] = useState('10:50');
  const [girlSelectedLocation, setGirlSelectedLocation] = useState(null);

  // Male details
  const [boyName, setBoyName] = useState('');
  //const [boyPhone, setBoyPhone] = useState('');
  //const [boyPhoneError, setBoyPhoneError] = useState('');
  const [boySelectedDate, setBoySelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [boySelectedTime, setBoySelectedTime] = useState('10:40');
  const [boySelectedLocation, setBoySelectedLocation] = useState(null);

  // Common phone number
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const [matchingData, setMatchingData] = useState(null);
  const [loading, setLoading] = useState(false);

  const { KUNDLI_MATCHING_API } = astrologyEndPoint;

  // Helper function to get user-friendly error messages for HTTP status codes
  const getStatusMessage = (status) => {
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please login and try again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Service not found. Please try again later.';
      case 409:
        return 'Conflict error. Please check your data and try again.';
      case 422:
        return 'Invalid data provided. Please check your input and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Service temporarily unavailable. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed with status ${status}. Please try again.`;
    }
  };

  // Phone number validation function based on backend rules
  const validatePhoneNumber = (phoneInput) => {
    if (!phoneInput || phoneInput.trim() === '') {
      return {
        isValid: false,
        formatted: '',
        error: 'Phone number is required'
      };
    }

    // Remove all non-digit characters
    let digits = phoneInput.replace(/\D/g, '');
    
    // Handle different prefixes
    if (digits.startsWith('91') && digits.length === 12) {
      // Remove +91 or 91 prefix
      digits = digits.substring(2);
    } else if (digits.startsWith('0') && digits.length === 11) {
      // Remove leading 0
      digits = digits.substring(1);
    }

    // Check if exactly 10 digits after formatting
    if (digits.length !== 10) {
      return {
        isValid: false,
        formatted: digits,
        error: 'Phone number must be exactly 10 digits'
      };
    }

    // Check if starts with 6, 7, 8, or 9
    const firstDigit = digits.charAt(0);
    if (!['6', '7', '8', '9'].includes(firstDigit)) {
      return {
        isValid: false,
        formatted: digits,
        error: 'Phone number must start with 6, 7, 8, or 9'
      };
    }

    return {
      isValid: true,
      formatted: digits,
      error: ''
    };
  };

  // Handle phone input change with validation
  const handlePhoneChange = (e, setter, setError) => {
    const inputValue = e.target.value;
    setter(inputValue);
    
    const validation = validatePhoneNumber(inputValue);
    setError(validation.error);
  };

  // Handle location selection for girl
  const handleGirlLocationSelect = (location) => {
    setGirlSelectedLocation(location);
  };

  // Handle location selection for boy
  const handleBoyLocationSelect = (location) => {
    setBoySelectedLocation(location);
  };

  const handleMatchMaking = async () => {
    // Validate all required fields
    if (!girlName.trim()) {
        toast.error(t('enterGirlsName'));
      return;
    }
    
    if (!boyName.trim()) {
        toast.error(t('enterBoysName'));
      return;
    }
    
    // Validate phone number
    const phoneValidation = validatePhoneNumber(phone);
    if (!phoneValidation.isValid) {
      toast.error(phoneValidation.error);
      setPhoneError(phoneValidation.error);
      return;
    }
    
    if (!girlSelectedLocation || !girlSelectedLocation.coordinates) {
        toast.error(t('pleaseSelectValidLocation'));
      return;
    }

    if (!boySelectedLocation || !boySelectedLocation.coordinates) {
        toast.error(t('pleaseSelectValidLocation'));
      return;
    }

    setLoading(true);
    setMatchingData(null);

    try {
      // Format datetime to match the exact format from curl example
      const girlDateTime = `${girlSelectedDate}T${girlSelectedTime}:40+00:00`;
      const boyDateTime = `${boySelectedDate}T${boySelectedTime}:40+00:00`;

      // Get language from localStorage or default to 'en'
      const currentLanguage = localStorage.getItem('language') || 'hi';

      // Build the URL exactly like the curl example
      const apiUrl = `${KUNDLI_MATCHING_API}?ayanamsa=1&girl_coordinates=${encodeURIComponent(girlSelectedLocation.coordinates)}&girl_dob=${encodeURIComponent(girlDateTime)}&boy_coordinates=${encodeURIComponent(boySelectedLocation.coordinates)}&boy_dob=${encodeURIComponent(boyDateTime)}&la=${currentLanguage}&girl_name=${encodeURIComponent(girlName.trim())}&boy_name=${encodeURIComponent(boyName.trim())}&phone=${encodeURIComponent(phoneValidation.formatted)}`;

      const response = await apiConnector(
        'GET',
        apiUrl,
        null,
        null,
        null
      );

      if (response?.data) {
        setMatchingData(response.data);
        toast.success(t('kundliMatchingCompleted'));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching kundli matching:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle different types of API errors with more specific messages
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // First, try to extract the actual error message from the response
        let errorMessage = null;
        
        // Check if the response data is a string (direct error message)
        if (typeof errorData === 'string') {
          errorMessage = errorData.trim();
        }
        // Check for specific error message fields
        else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (errorData.errors) {
          // Handle validation errors array
          if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors.join(', ');
          } else {
            errorMessage = JSON.stringify(errorData.errors);
          }
        } else if (typeof errorData === 'object' && errorData !== null) {
          // If it's an object, try to find any string value that looks like an error message
          const stringValues = Object.values(errorData).filter(val => typeof val === 'string' && val.trim().length > 0);
          if (stringValues.length > 0) {
            errorMessage = stringValues[0];
          }
        }
        
        // Display the error message if we found one
        if (errorMessage) {
          toast.error(errorMessage);
        } else {
          // If we have response data but no clear message, show the status
          const statusMessage = getStatusMessage(error.response.status);
          toast.error(statusMessage);
        }
      } else if (error.response?.status) {
        // Handle HTTP status codes without specific error data
        const statusMessage = getStatusMessage(error.response.status);
        toast.error(statusMessage);
      } else if (error.message) {
        // Handle network errors or other issues
        if (error.message.includes('Network Error') || error.message.includes('timeout')) {
          toast.error('Network error. Please check your internet connection and try again.');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('Failed to perform kundli matching. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      id="kundli-matching"
      className="bg-white rounded-2xl shadow-lg p-8 mb-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      whileHover={{ scale: 1.02, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
    >
      <motion.h3 
        className="text-2xl font-bold text-orange-800 mb-6 text-center flex items-center justify-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <FaHeart className="mr-3 text-red-500" /        >
          {t('kundliMatching')}
        <FaHeart className="ml-3 text-red-500" />
      </motion.h3>

      {/* Phone Number Section */}
      <div className="mb-8 p-6 bg-gradient-to-r from-pink-50 to-blue-50 rounded-xl border-2 border-pink-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">
          <FaPhone className="inline mr-2" />
            {t('contactInformation')}
        </h4>
        <div className="max-w-md mx-auto">
          <input
            type="tel"
            value={phone}
            onChange={(e) => handlePhoneChange(e, setPhone, setPhoneError)}
            placeholder={t('enterContactPhone')}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
              phoneError ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {phoneError && (
            <p className="text-red-500 text-sm mt-1">{phoneError}</p>
          )}
          {phone && !phoneError && (
            <p className="text-green-600 text-sm mt-1">{t('validPhoneNumber')}</p>
          )}
        </div>
      </div>

      {/* Male and Female Details Section - Side by Side */}
      <div className="mb-8 grid lg:grid-cols-2 gap-6">
        {/* Female Details Section - Left */}
        <div className="p-6 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl border-2 border-pink-200">
          <h4 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
            <FaVenus className="mr-2 text-pink-500" />
              {t('femaleDetails')}
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaUser className="inline mr-2" />
                  {t('fullName')}
              </label>
              <input
                type="text"
                value={girlName}
                onChange={(e) => setGirlName(e.target.value)}
                placeholder="Enter girl's full name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaCalendarAlt className="inline mr-2" />
                  {t('dateOfBirth')}
              </label>
              <input
                type="date"
                value={girlSelectedDate}
                onChange={(e) => setGirlSelectedDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaClock className="inline mr-2" />
                  {t('timeOfBirth')}
              </label>
              <input
                type="time"
                value={girlSelectedTime}
                onChange={(e) => setGirlSelectedTime(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaMapMarkerAlt className="inline mr-2" />
                  {t('placeOfBirth')}
              </label>
              <HardcodedLocationSelector
                value={girlSelectedLocation?.name || ''}
                onChange={handleGirlLocationSelect}
                  placeholder={t('searchForGirlsBirthPlace')}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Male Details Section - Right */}
        <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200">
          <h4 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
            <FaMars className="mr-2 text-blue-500" />
              {t('maleDetails')}
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaUser className="inline mr-2" />
                  {t('fullName')}
              </label>
              <input
                type="text"
                value={boyName}
                onChange={(e) => setBoyName(e.target.value)}
                placeholder="Enter boy's full name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaCalendarAlt className="inline mr-2" />
                  {t('dateOfBirth')}
              </label>
              <input
                type="date"
                value={boySelectedDate}
                onChange={(e) => setBoySelectedDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaClock className="inline mr-2" />
                  {t('timeOfBirth')}
              </label>
              <input
                type="time"
                value={boySelectedTime}
                onChange={(e) => setBoySelectedTime(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FaMapMarkerAlt className="inline mr-2" />
                  {t('placeOfBirth')}
              </label>
              <HardcodedLocationSelector
                value={boySelectedLocation?.name || ''}
                onChange={handleBoyLocationSelect}
                  placeholder={t('searchForBoysBirthPlace')}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Match Button */}
      <motion.div 
        className="text-center mb-6"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <motion.button
          onClick={handleMatchMaking}
          disabled={loading}
          className="bg-gradient-to-r from-pink-500 to-blue-500 text-white px-8 py-3 rounded-full font-semibold hover:from-pink-600 hover:to-blue-600 transition-all disabled:opacity-50"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}
          whileTap={{ scale: 0.95 }}
        >
          {loading ? (
            <>
                <FaSpinner className="animate-spin inline mr-2" /> {t('matchingKundlis')}
            </>
          ) : (
            <>
                <FaHeart className="inline mr-2" /> {t('checkCompatibility')}
            </>
          )}
        </motion.button>
      </motion.div>

      {/* Results */}
      {matchingData?.data && (
        <motion.div 
          className="mt-8"
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <HoroscopeMatchingReport data={matchingData.data} />
        </motion.div>
      )}

    </motion.div>
  );
};

export default MatchMakingFeature;